import { BrushHair } from './BrushHair';
import { hexToHsl, hslToHex, drawPathPortion } from './utils';
import { ShapePoint } from './ShapePoint';
import { BrushSprayPaint } from './BrushSprayPaint';
import { BrushStamps } from './BrushStamps';

const IS_MOBILE = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const EASE = 0.1;
const RADIUS_VALUES_MIN = 1;
const RADIUS_VALUES_MAX = 70;
const SCALE = window.devicePixelRatio || 1;
const HAIRS_COUNT = IS_MOBILE ? 20 : 50;
const TWO_PI = Math.PI * 2;
const ANGLE_RAD_180 = Math.PI;
const GLOBAL_SIZE_MULTIPLIER = 0.6;

class BrushCanvas {
  constructor(
    container,
    shapes,
    drawMaxDuration,
    onDrawStart = () => {},
    onDrawEnd = () => {}
  ) {
    this.hairs = [];
    this.hexColor = '';
    this.color = { h: 0, s: 0, l: 0.1 };
    this.ease = EASE;
    this.shapes = shapes;
    this.drawMaxDuration = drawMaxDuration;
    this.introMaxSpeed = 0.2;
    this.onDrawStart = onDrawStart;
    this.onDrawEnd = onDrawEnd;
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.isPlayingIntro = true;
    this.isDrawing = true;
    this.mouse = { x: 0, y: 0, speed: 0, isDown: false };
    this.center = { x: 0, y: 0 };
    this.canvasCenter = { x: 0, y: 0 };
    this.startPoint = { x: 0, y: 0, time: 0 };
    this.isActivated = false;
    this.distanceProgress = 0;
    this.container = container;
    this.drawDistance = 0;
    this.radiusMultiplier = 1;
    this.instrumentIndex = 0;
    this.isInstrumentStamp = false;
    this.instrumentName = 'flute';
    this.hairsOpacity = 0.8;
    this.limitMultiplier = 1;
    this.canvas = document.createElement('canvas');
    this.canvas.classList.add('painting');
    this.container.appendChild(this.canvas);
    this.canvasContext = this.canvas.getContext('2d');
    this.timestamp = 0;
    this.drawTime = 0;
    this.redrawTimeout = null;
    this.redrawHandler = this.redraw.bind(this);
    this.sprayPaint = new BrushSprayPaint();
    this.stamps = new BrushStamps();
    this.isUsingSpray = false;

    this.baseHairLength = 1;
    this.instrumentHairLengthFactor = 1;

    this.sharedImageBackground = null;

    this.setup();
  }

  resize() {
    if (this.redrawTimeout) {
      clearTimeout(this.redrawTimeout);
      this.redrawTimeout = null;
    }

    this.width = window.innerWidth;
    this.height = window.innerHeight;

    this.canvas.width = this.width * SCALE;
    this.canvas.height = this.height * SCALE;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.canvasCenter.x = this.canvas.width / 2;
    this.canvasCenter.y = this.canvas.height / 2;

    this.center.x = this.canvasCenter.x;
    this.center.y = this.canvasCenter.y;

    if (this.sharedImageBackground) {
      this.drawSharedImage(this.sharedImageBackground);
    } else {
      this.redrawTimeout = setTimeout(this.redrawHandler, 300);
    }
  }

  redraw() {
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasContext.globalAlpha = 1;
    if (this.shapes.history.length) {
      const lastImageData =
        this.shapes.history[this.shapes.history.length - 1].imageData;
      if (lastImageData && 'image' in lastImageData) {
        this.canvasContext.drawImage(
          lastImageData.image,
          this.canvasCenter.x - lastImageData.width / 2,
          this.canvasCenter.y - lastImageData.height / 2
        );
      }
    }
  }

  drawSharedImage(img) {
    this.sharedImageBackground = img;
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasContext.globalAlpha = 1;
    this.canvasContext.drawImage(
      img,
      this.canvasCenter.x - img.width / 2,
      this.canvasCenter.y - img.height / 2
    );
  }

  setAlpha(value) {
    this.canvas.style.opacity = value;
  }

  setup() {
    for (var i = 0; i < 100; i++) {
      const hair = new BrushHair({
        length: 10 + Math.random() * 10,
        thickness: 1,
        color: this.color,
        spacing: Math.random() * 0.8 + 0.2,
        friction: 0.7 + Math.random() * 0.18,
        radius: 0.05 + Math.random() * 0.95,
      });
      hair.move(this.center.x, this.center.y, true);
      this.hairs.push(hair);
    }
    this.stamps.load();
  }

  start() {
    this.isPlayingIntro = false;
    this.clear();
    this.mouse.isDown = false;
    this.distanceProgress = 0;
    this.radiusMultiplier = 1;
  }

  deactivate() {
    this.isActivated = false;
    this.stopDrawing(false);
  }

  activate() {
    this.isActivated = true;
  }

  move(e) {
    if (e.touches && e.touches.length) {
      this.mouse.x = e.touches[0].clientX;
      this.mouse.y = e.touches[0].clientY;
    } else if (!e.touches) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    }
  }

  movePen(x, y) {
    this.mouse.x = x;
    this.mouse.y = y;
  }

  downPen() {
    this.center.x = this.mouse.x * SCALE;
    this.center.y = this.mouse.y * SCALE;
    this.startDrawing();
  }

  upPen() {
    this.stopDrawing(false);
  }

  startDrawing(e) {
    if (e) {
      this.move(e);
      this.center.x = this.mouse.x * SCALE;
      this.center.y = this.mouse.y * SCALE;
    }

    this.isDrawing = true;
    const point = new ShapePoint({
      x: this.center.x - this.canvasCenter.x,
      y: this.center.y - this.canvasCenter.y,
      screenY: this.center.y / this.canvas.height,
      isStamp: this.isInstrumentStamp,
      time: this.timestamp,
      loopTime: this.loopTime,
      color: { h: this.color.h, s: this.color.s, l: this.color.l },
      hexColor: this.hexColor,
      instrument: this.instrumentIndex,
    });
    if (this.isInstrumentStamp) {
      this.stamps.draw(
        this.canvasContext,
        this.center.x,
        this.center.y,
        this.hexColor,
        this.instrumentName
      );
      this.shapes.addStamp(point, this.color);
      this.onDrawStart(point);
    } else {
      if (!this.mouse.isDown) {
        this.shapes.startStroke(point, this.isPlayingIntro);
        this.onDrawStart(point);
        this.startPoint.x = this.center.x;
        this.startPoint.y = this.center.y;
        this.startPoint.time = this.timestamp;
        this.drawDistance = 0;
      }
      this.distanceProgress = 0;
      this.mouse.isDown = true;
    }
  }

  stopDrawing(dispatching = true) {
    this.isDrawing = false;
    if (dispatching && this.mouse.isDown) {
      this.onDrawEnd();
    }
    this.mouse.isDown = false;
    this.distanceProgress = 0;
  }

  down(e) {
    if (this.isDrawing) {
      this.upPen();
    }
    if (this.isActivated) {
      this.startDrawing(e);
    }
  }

  up(e = null) {
    this.stopDrawing();
  }

  changeInstrument(
    index,
    id,
    color = null,
    isStamp = false,
    hairLengthFactor = 1,
    brushOptions = null
  ) {
    this.instrumentIndex = index;
    this.instrumentName = id;
    this.isInstrumentStamp = isStamp;
    if (color) {
      this.hexColor = color;
      const hsl = hexToHsl(color);
      this.color.h = hsl[0];
      this.color.s = hsl[1];
      this.color.l = hsl[2];
    }

    if (!this.isInstrumentStamp) {
      this.instrumentHairLengthFactor = hairLengthFactor;
      this.updateBrushOptions(brushOptions);
    }
  }

  updateBrushOptions(brushOptions = null) {
    const isChangingHairs =
      brushOptions && !brushOptions.isSpray ? true : false;
    if (isChangingHairs) {
      this.isUsingSpray = false;
      this.hairLength = brushOptions.hairLength;
      this.hairsOpacity = brushOptions.hairsOpacity;
      this.limitMultiplier = brushOptions.limitMultiplier;
    }
    this.baseHairLength = this.hairLength * this.instrumentHairLengthFactor;
    if (isChangingHairs) {
      for (const hair of this.hairs) {
        hair.changeColor(this.color);
        if (brushOptions) {
          hair.changeOptions(brushOptions, this.baseHairLength);
        }
      }
    } else if (brushOptions && brushOptions.isSpray) {
      this.sprayPaint.flow = brushOptions.sprayFlow;
      this.isUsingSpray = true;
    }
  }

  changeAtmosphere(atmosphereData) {
    this.atmosphereId = atmosphereData.id;
    this.updateBrushOptions(atmosphereData.brush);
  }

  update(timestamp, loopTime) {
    const isDrawing = this.mouse.isDown && this.isActivated ? true : false;
    const delta = timestamp - this.timestamp;
    const lastX = this.center.x;
    const lastY = this.center.y;
    this.timestamp = timestamp;
    this.loopTime = loopTime;
    this.drawTime = this.timestamp - this.startPoint.time;
    // ease mouse position
    this.ease += (0.7 - this.ease) * 0.05;
    this.center.x += (this.mouse.x * SCALE - this.center.x) * this.ease;
    this.center.y += (this.mouse.y * SCALE - this.center.y) * this.ease;
    const mouseDist = Math.sqrt(
      Math.pow(lastX - this.center.x, 2) + Math.pow(lastY - this.center.y, 2)
    );
    this.mouse.speed = mouseDist / delta / 15;
    if (this.isPlayingIntro && this.mouse.speed > this.introMaxSpeed) {
      this.mouse.speed = this.introMaxSpeed;
    }
    let distanceRadiusProgress = 0;
    if (isDrawing) {
      const dist = Math.sqrt(
        Math.pow(this.startPoint.x - this.center.x, 2) +
          Math.pow(this.startPoint.y - this.center.y, 2)
      );
      this.drawDistance = Math.max(this.drawDistance, dist);
      this.distanceProgress = Math.min(1, this.drawDistance / 300);
      distanceRadiusProgress = Math.min(1, this.drawDistance / 50);
    }

    const radius =
      Math.min(
        RADIUS_VALUES_MAX,
        (RADIUS_VALUES_MIN +
          (RADIUS_VALUES_MAX - RADIUS_VALUES_MIN) * this.mouse.speed) *
          distanceRadiusProgress
      ) *
      this.radiusMultiplier *
      GLOBAL_SIZE_MULTIPLIER;

    if (this.isUsingSpray) {
      this.sprayPaint.update();
    } else {
      const step = TWO_PI / HAIRS_COUNT;
      const hairLimit = Math.min(
        this.drawDistance / this.baseHairLength,
        Math.max(4, this.distanceProgress * radius * this.limitMultiplier)
      );

      for (let i = 0, n = HAIRS_COUNT; i < n; i++) {
        const hair = this.hairs[i];
        const theta = i * step;
        const px = Math.cos(theta + ANGLE_RAD_180) * radius * 0.8;
        const py = Math.sin(theta + ANGLE_RAD_180) * radius * 0.8;
        hair.limit = hairLimit; // Hair length limit
        hair.move(this.center.x + px, this.center.y + py);
        hair.update();
      }
    }

    if (this.drawTime >= this.drawMaxDuration && !this.isPlayingIntro) {
      this.up();
    } else if (isDrawing) {
      this.draw(radius);
    }
  }

  draw(radius) {
    const point = new ShapePoint({
      x: this.center.x - this.canvasCenter.x,
      y: this.center.y - this.canvasCenter.y,
      screenY: this.center.y / this.canvas.height,
      isStamp: false,
      p1x: this.center.x - this.canvasCenter.x,
      p1y: this.center.y - this.canvasCenter.y,
      p2x: this.center.x - this.canvasCenter.x,
      p2y: this.center.y - this.canvasCenter.y,
      hairs: [],
      radius: radius,
      time: this.timestamp,
      loopTime: this.loopTime,
      color: { h: this.color.h, s: this.color.s, l: this.color.l },
      hexColor: this.hexColor,
      instrument: this.instrumentIndex,
    });
    this.shapes.addPoint(point);

    // Ribbon
    if (point.index > 0 && !this.isUsingSpray) {
      this.canvasContext.globalAlpha = 1;
      drawPathPortion(
        this.shapes.getPreviousPoint(point),
        point,
        this.canvasContext
      );

      // Hairs
      this.canvasContext.globalAlpha = this.hairsOpacity;
      for (const hair of this.hairs) {
        hair.draw(this.canvasContext);
        point.hairs.push({
          outer: hair.outer.map((point) => ({
            x: point.x - this.canvasCenter.x,
            y: point.y - this.canvasCenter.y,
          })),
          inner: hair.inner.map((point) => ({
            x: point.x - this.canvasCenter.x,
            y: point.y - this.canvasCenter.y,
          })),
        });
      }
    }

    if (this.isUsingSpray) {
      this.sprayPaint.draw(
        this.canvasContext,
        this.center.x,
        this.center.y,
        this.hexColor,
        Math.max(46, radius * 7) * SCALE
      );
    }
  }

  clear() {
    this.distanceProgress = 0;
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
export { BrushCanvas };
